/**
 * The chronological moment stream. Moments after the playhead render dimmed
 * ("hasn't happened yet"); the most recent moment at-or-before the playhead
 * is highlighted and kept in view. Clicking a row seeks the playhead to it.
 * Rows backed by a navigable entity (a view, a task) reveal an "Open" link
 * on hover/focus that jumps straight to it.
 */
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, Tag } from "@scribenote/brian-design-system";
import { useNavigate } from "@tanstack/react-router";
import { formatTime } from "@utils/format-date";
import { useEffect, useRef } from "react";

import type { ReplayMoment } from "./replayClient";
import {
  ACTOR_INK,
  ACTOR_LABEL,
  categoryInk,
  CATEGORY_LABEL,
} from "./replayTheme";
import { ms } from "./timeScale";

/** Ink link, per PatientCard's flow links: indigo is reserved for Brian and
 * focus rings, and this is plain navigation, so the link is a pen-underlined
 * note on paper. */
const INK_LINK =
  "flex cursor-pointer items-center gap-1.5 rounded-paper text-xs font-medium text-ink-body underline decoration-ink/30 decoration-1 underline-offset-2 transition-colors hover:text-ink hover:decoration-ink motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

export const MomentFeed = ({
  moments,
  playhead,
  onSeek,
}: {
  moments: ReplayMoment[];
  playhead: number;
  onSeek: (t: number) => void;
}) => {
  const navigate = useNavigate();
  // The workspace's SavedView overlay is a true deep link; tasks only have
  // the list page, so a task moment lands there.
  const openEntity = (moment: ReplayMoment) => {
    if (moment.entityKind === "view" && moment.entityUuid) {
      void navigate({ to: "/", search: { view: moment.entityUuid } });
    } else if (moment.entityKind === "task") {
      void navigate({ to: "/tasks", search: {} });
    }
  };
  const openLabel = (moment: ReplayMoment): string | null => {
    if (moment.entityKind === "view" && moment.entityUuid) return "Open view";
    if (moment.entityKind === "task") return "Open tasks";
    return null;
  };

  // Index of the newest moment that has already "happened".
  let currentIndex = -1;
  for (let i = 0; i < moments.length; i++) {
    if (ms(moments[i].at) <= playhead) currentIndex = i;
    else break;
  }

  // Keyed on the current moment's identity, not just its index — a filter
  // toggle can put a different moment at the same index and still must
  // re-scroll the highlight into view.
  const current = currentIndex >= 0 ? moments[currentIndex] : null;
  const currentRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    currentRef.current?.scrollIntoView({
      block: "nearest",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [currentIndex, current?.at, current?.kind]);

  if (moments.length === 0) {
    return (
      <div className="p-6 text-sm text-annotation">
        Nothing recorded for this visit in the categories you picked.
      </div>
    );
  }

  return (
    <ol className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
      {moments.map((moment, i) => {
        const happened = i <= currentIndex;
        const isCurrent = i === currentIndex;
        const label = openLabel(moment);
        return (
          <li
            key={`${moment.at}-${moment.kind}-${i}`}
            ref={isCurrent ? currentRef : undefined}
            className="group relative"
          >
            <button
              type="button"
              onClick={() => onSeek(ms(moment.at))}
              // The current moment is the sheet held up off the page, framed
              // in ink — "you are reading this one" is a selection, not a
              // status, so it takes no status color.
              className={cn(
                "flex w-full cursor-pointer items-start gap-2.5 rounded-paper border px-2.5 py-1.5 text-left",
                "transition duration-150 ease-paper motion-reduce:transition-none",
                "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
                isCurrent
                  ? "border-ink bg-card shadow-lift"
                  : "border-transparent hover:bg-recess",
                happened ? "" : "opacity-40",
              )}
            >
              <span className="w-14 shrink-0 pt-0.5 font-mono text-[11px] tabular-nums text-annotation">
                {formatTime(moment.at)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-ink">
                    {moment.title}
                  </span>
                  <Tag
                    className="shrink-0"
                    data-testid={`replay-moment-badge-${moment.category}`}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mr-1 inline-block size-1.5 rounded-full align-middle",
                        categoryInk(moment.category).dot,
                      )}
                    />
                    {CATEGORY_LABEL[moment.category]}
                  </Tag>
                </span>
                {moment.detail && (
                  <span className="mt-0.5 block truncate text-xs text-annotation">
                    {moment.detail}
                  </span>
                )}
              </span>
              <span
                className={cn(
                  "shrink-0 pt-0.5 text-[11px]",
                  ACTOR_INK[moment.actorKind].text,
                )}
              >
                {moment.actorName ?? ACTOR_LABEL[moment.actorKind]}
              </span>
            </button>
            {label && (
              // Sibling of the seek button (a link nested inside it would be
              // invalid HTML); floats over the actor label only while the row
              // is hovered or either control has focus.
              <span className="absolute top-1/2 right-2 -translate-y-1/2 rounded-paper bg-card px-1.5 py-0.5 opacity-0 shadow-lift transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none">
                <button
                  type="button"
                  onClick={() => openEntity(moment)}
                  data-testid={`replay-open-${moment.entityKind}`}
                  className={INK_LINK}
                >
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    aria-hidden="true"
                    className="text-[10px]"
                  />
                  {label}
                </button>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
};
